import React from 'react';
import { JOB_ADVANCEMENTS } from '../../../../shared/classes/JobAdvancements';
import { Tooltip } from '../../components/Tooltip';
import type { JobPath, JobsResult } from '../../hooks/useJobs';
import { TomeButton, TomeChapter, TomeChip, TomeEntry, TomeFolio, type TomeTone } from '../../primitives/tome';
import { formatCallingName, getCallingMeta } from '../../theme';
import './TalentsTome.css';

const STAT_LABELS: Record<string, string> = {
  strength: 'STR', agility: 'AGI', intelligence: 'INT', wisdom: 'WIS', defense: 'DEF',
  attack: 'ATK', dexterity: 'DEX', vitality: 'VIT', criticalRate: 'CRIT',
};

function formatStatBonuses(bonuses?: Record<string, number>): string {
  if (!bonuses) return '';
  return Object.entries(bonuses).map(([stat, value]) => {
    const formatted = typeof value === 'number' && value < 1 && value > 0 ? `+${Math.round(value * 100)}%` : `+${value}`;
    return `${formatted} ${STAT_LABELS[stat] ?? stat.toUpperCase()}`;
  }).join(' · ');
}

type PathState = 'current' | 'mastered' | 'ready' | 'locked';

const PATH_BADGE: Record<PathState, { tone: TomeTone; text: (job: JobPath) => string }> = {
  current: { tone: 'active', text: () => 'Current Specialization' },
  mastered: { tone: 'completed', text: () => 'Mastered' },
  ready: { tone: 'ready', text: () => 'Ready to Advance' },
  locked: { tone: 'locked', text: job => `Requires Level ${job.requiredLevel}` },
};

const PathEntry: React.FC<{ job: JobPath; state: PathState; pending: boolean; busy: boolean; onAdvance: () => void }> =
  ({ job, state, pending, busy, onAdvance }) => {
    const advancement = JOB_ADVANCEMENTS[job.id];
    const stats = formatStatBonuses(advancement?.statBonuses);
    const badge = PATH_BADGE[state];
    return <TomeEntry className="talents-tome__entry" tone={badge.tone} shapeSeed={job.id} density="compact" title={job.name}
      kicker={`Level ${job.requiredLevel} path`} data-testid={`job-card-${job.id}`} data-path-state={state}
      status={<TomeChip tone={badge.tone} cut="clean">{badge.text(job)}</TomeChip>}>
      <p className="talents-tome__description">{job.description}</p>
      {stats && <p className="talents-tome__effects">Bonus: {stats}</p>}
      {advancement?.abilities?.length ? <ul className="jobs-tome__abilities" aria-label={`Abilities of ${job.name}`}>
        {advancement.abilities.map(ability => <li key={ability.id}>
          <Tooltip data={{ title: ability.name, description: ability.description }}>
            <span className="jobs-tome__ability" tabIndex={0}>{ability.name}</span>
          </Tooltip>
        </li>)}
      </ul> : null}
      {state === 'ready' && <footer className="talents-tome__entry-foot">
        <span>The server confirms each advance.</span>
        <TomeButton cut="clean" disabled={busy} onClick={onAdvance} data-testid={`advance-button-${job.id}`}>
          {pending ? 'Advancing…' : `Advance to ${job.name}`}</TomeButton>
      </footer>}
    </TomeEntry>;
  };

/**
 * The Calling page on the Tome: a folio for who you are, then the paths ahead as entries in order of
 * level. Which job you hold is the server's; the button only asks, and waits for the answer. The
 * TomeWindow frame supplies the parchment and the zone's tokens, and scrolls the page.
 */
export const JobsPage: React.FC<{ jobs: JobsResult }> = ({ jobs }) => {
  const { currentJob, className, level, paths, advanceJob, pendingJob = null, notice = null } = jobs;
  const calling = getCallingMeta(className);
  const specialty = currentJob ? JOB_ADVANCEMENTS[currentJob] : undefined;
  const currentRequiredLevel = specialty?.requiredLevel ?? 1;
  const stateOf = (job: JobPath): PathState => currentJob === job.id ? 'current'
    : currentRequiredLevel > job.requiredLevel ? 'mastered' : job.available ? 'ready' : 'locked';

  return <section className="talents-tome" aria-label="Your calling">
    <TomeFolio className="talents-tome__folio" aria-label="Who you are" shapeSeed={className ?? 'jobs'} data-testid="jobs-header">
      <div className="jobs-tome__identity">
        <span className="talents-tome__art" aria-hidden="true">{calling?.icon ?? '◇'}</span>
        <div>
          <span className="talents-tome__kicker">Your calling</span>
          <div className="talents-tome__heading"><h3>{className ? formatCallingName(className) : 'No character selected'}</h3></div>
          <p className="jobs-tome__level">Level {level}{specialty && specialty.requiredLevel > 1 ? ` · ${specialty.name}` : ''}</p>
        </div>
      </div>
      {calling && <p className="jobs-tome__calling">{calling.description}</p>}
    </TomeFolio>
    <TomeChapter title="Paths ahead" count={paths.length || undefined}>
      <p className="jobs-tome__note">Advance your calling to unlock powerful class specializations, unique combat abilities, and stat enhancements.</p>
      {paths.map(job => <PathEntry key={job.id} job={job} state={stateOf(job)} pending={pendingJob === job.id} busy={pendingJob !== null}
        onAdvance={() => advanceJob(job.id)} />)}
      {className && paths.length === 0 && <p className="talents-tome__empty">No further paths are listed for this calling.</p>}
    </TomeChapter>
    {notice && <p className="talents-tome__feedback" role={notice.tone === 'error' ? 'alert' : 'status'}
      data-refused={notice.tone === 'error' || undefined}>{notice.text}</p>}
  </section>;
};
